import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getProgramBySlug } from '../../data/programs';
import { getProgress } from '../../lib/storage/progressStore';
import { track } from '../../lib/analytics/track';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../services/apiClient';
import Navbar from '../../components/Navbar';
import SEOHead from '../../components/SEOHead';
import FinalExercise from '../../components/programs/FinalExercise';
import { CheckCircle2, Circle, ArrowLeft, ArrowRight, Check, Lock, Headphones, Play, Award } from 'lucide-react';

export default function ProgramLanding() {
  const { programSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const program = getProgramBySlug(programSlug);
  const [copied, setCopied] = useState(false);
  // Badge ya emitido para este programa + usuario
  const [existingBadge, setExistingBadge] = useState(null);
  const [badgeChecked, setBadgeChecked] = useState(false);

  // Consultar backend al montar si el usuario ya tiene badge para este programa
  useEffect(() => {
    if (!isAuthenticated || !program?.badgeEnabled || badgeChecked) return;
    setBadgeChecked(true);
    apiClient.get(`/badges/by-program/${program.slug}`)
      .then(res => {
        if (res.data?.hasBadge) setExistingBadge(res.data.badge);
      })
      .catch(() => { /* sin badge o error — ignorar */ });
  }, [isAuthenticated, program, badgeChecked]);

  useEffect(() => {
    if (program) {
      track('program_view', { programSlug: program.slug });
    }
  }, [program]);

  const handleShare = async () => {
    const shareData = {
      title: program.title,
      text: `${program.outcome}`,
      url: window.location.href
    };

    try {
      // Web Share API (nativo móvil)
      if (navigator.share) {
        await navigator.share(shareData);
        track('program_shared', { programSlug: program.slug, method: 'native' });
      } else {
        // Fallback: copiar URL
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        track('program_shared', { programSlug: program.slug, method: 'clipboard' });
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error sharing:', err);
      }
    }
  };

  if (!program) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Programa no encontrado
            </h1>
            <Link
              to="/programas"
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              ← Volver a programas
            </Link>
          </div>
        </div>
      </>
    );
  }

  const progress = getProgress(program.slug);
  const completedUnits = program.units.filter(
    (u) => progress[u.slug]?.completedAt
  ).length;
  const totalUnits = program.units.length;

  // Encontrar primera unidad no completada
  const nextUnit = program.units.find((u) => !progress[u.slug]?.completedAt);

  // Schema.org para curso individual
  const courseSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "name": "Breadcrumbs",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": "https://edutechpulselab.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Micro-Programas",
            "item": "https://edutechpulselab.com/programas"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": program.title,
            "item": `https://edutechpulselab.com/p/${program.slug}`
          }
        ]
      },
      {
        "@type": "Course",
        "name": program.title,
        "description": program.outcome,
        "provider": {
          "@type": "EducationalOrganization",
          "name": "EduTech Pulse Lab",
          "url": "https://edutechpulselab.com"
        },
        "educationalLevel": "Executive",
        "timeRequired": `PT${program.durationMin}M`,
        "numberOfCredits": totalUnits,
        "inLanguage": "es-ES",
        "url": `https://edutechpulselab.com/p/${program.slug}`,
        ...(program.sources && {
          "isBasedOn": program.sources.map(source => ({
            "@type": "Book",
            "name": source.book,
            "author": {
              "@type": "Person",
              "name": source.author,
              "affiliation": {
                "@type": "Organization",
                "name": source.institution
              }
            }
          }))
        }),
        "hasCourseInstance": {
          "@type": "CourseInstance",
          "courseMode": "online",
          "courseWorkload": `PT${program.durationMin}M`,
          "instructor": {
            "@type": "Organization",
            "name": "EduTech Pulse Lab"
          }
        },
        "syllabusSections": program.units.map((unit, index) => ({
          "@type": "Syllabus",
          "position": index + 1,
          "name": unit.title,
          "description": unit.objective
        }))
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={`${program.title} - Micro-Programa ${program.programType === 'ejecutivo' ? 'Ejecutivo' : 'Editorial'}`}
        description={`${program.outcome} Basado en metodologías de ${program.sources ? program.sources.map(s => `${s.author} (${s.book})`).join(', ') : 'expertos'}. Programa de ${program.durationMin} minutos con ${totalUnits} unidades prácticas.`}
        canonicalUrl={`/p/${program.slug}`}
        ogType="article"
        schema={courseSchema}
        robots="index, follow, max-image-preview:large, max-snippet:-1"
      />
      <Navbar />
      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        
        {/* ===== HEADER - Hero Section ===== */}
        <section className="relative border-b bg-white border-slate-200">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-12">
            {/* Breadcrumb desktop */}
            <div className="hidden md:flex items-center gap-2 mb-6 text-sm">
              <Link to="/" className="text-slate-500 hover:text-cyan-600">Inicio</Link>
              <span className="text-slate-300">/</span>
              <Link to="/programas" className="text-slate-500 hover:text-cyan-600">Programas</Link>
              <span className="text-slate-300">/</span>
              <span className="text-slate-900">{program.title}</span>
            </div>

            {existingBadge ? (
              /* ── ESTADO LOGRO: usuario con insignia emitida ── */
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                <div className="flex-1 min-w-0">
                  {/* Pill de tipo */}
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                    Micro-programa {program.programType || 'editorial'} · <span className="text-emerald-600">Completado</span>
                  </p>
                  {/* Título */}
                  <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-3 tracking-tight leading-tight text-slate-900">
                    {program.title}
                  </h1>
                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 max-w-2xl">
                    {program.tagline}
                  </p>
                  {/* Badge-botón único: logro + CTA consolidados en un solo elemento */}
                  <Link
                    to={`/home/certificates/${existingBadge.verificationId}`}
                    className="inline-flex items-center gap-4 w-full sm:w-auto bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 rounded-2xl px-5 py-4 transition-all group"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold mb-0.5">Insignia obtenida</p>
                      <p className="text-sm font-bold text-slate-900 leading-snug truncate">{existingBadge.badgeLabelSnapshot}</p>
                      {existingBadge.claimedAt && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(existingBadge.claimedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold text-slate-400 group-hover:text-cyan-700 transition-colors">
                      Ver certificado
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                </div>
              </div>
            ) : (
              /* ── ESTADO POR DEFECTO: usuario sin badge ── */
              <>
                {/* Badge descriptor */}
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
                  Micro-programa {program.programType || 'editorial'}
                </p>
                {/* Título principal */}
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight text-slate-900">
                  {program.title}
                </h1>
                {/* Tagline */}
                <p className="text-base sm:text-xl text-slate-700 leading-relaxed mb-6 max-w-3xl">
                  {program.tagline}
                </p>
                {/* CTA */}
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href="#primer-audio"
                    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg text-base"
                  >
                    Empezar ahora ({program.durationMin} min total)
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
                {/* T09 — Aviso insignia */}
                {program.badgeEnabled && (
                  <p className="mt-4 text-xs text-slate-500 leading-relaxed max-w-xl">
                    Acceso libre al contenido. La insignia digital se desbloquea al completar el ejercicio final y requiere iniciar sesión para guardarla.
                  </p>
                )}
              </>
            )}

            {/* Progreso — solo para usuarios sin badge (con badge el logro ya está claro) */}
            {completedUnits > 0 && !existingBadge && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-600 mb-3">
                  Progreso: {completedUnits} de {totalUnits} conversaciones
                </p>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden w-48">
                  <div 
                    className="h-full bg-cyan-600 rounded-full transition-all duration-500"
                    style={{ width: `${(completedUnits / totalUnits) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ===== UNIDADES DISPONIBLES - Audio Destacado ===== */}
        <section id="primer-audio" className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-16">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              {program.units.filter(u => u.locked !== true).length === 1
                ? 'Empieza por esta conversación'
                : 'Conversaciones disponibles'}
            </h2>
            <p className="text-base text-slate-600">
              {program.units.filter(u => u.locked !== true).length === 1
                ? '15 minutos para explorar cómo pensar con claridad cuando la información es incompleta.'
                : `${program.units.filter(u => u.locked !== true).length} conversaciones listas para escuchar.`}
            </p>
          </div>

          <div className="grid gap-6">
          {program.units.filter(u => u.locked !== true).map((unit, index) => {
            const isCompleted = !!progress[unit.slug]?.completedAt;
            return (
              <Link
                key={unit.slug}
                to={`/p/${program.slug}/u/${unit.slug}`}
                className="block rounded-2xl overflow-hidden border border-cyan-200 shadow-lg hover:shadow-xl transition-all duration-200 group"
                style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 40%, #f8fafc 100%)' }}
              >
                {/* Franja superior de color - señal visual de audio */}
                <div className="h-1.5 w-full bg-gradient-to-r from-cyan-500 to-blue-600" />

                <div className="relative px-4 sm:px-8 pt-4 sm:pt-8 pb-4 sm:pb-6">
                  {isCompleted && (
                    <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span className="hidden xs:inline">Completada</span>
                    </div>
                  )}

                  {/* MÓVIL: layout horizontal compacto */}
                  <div className="flex items-center gap-3 sm:gap-6 mb-3 sm:mb-6">
                    {/* Botón Play — más pequeño en móvil */}
                    <div className="flex-shrink-0 w-11 h-11 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                      <Play className="w-5 h-5 sm:w-7 sm:h-7 text-white fill-white ml-0.5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Badge duración */}
                      <div className="inline-flex items-center gap-1.5 bg-white border border-cyan-200 text-cyan-700 text-xs font-semibold px-2.5 py-0.5 rounded-full mb-1.5 shadow-sm">
                        <Headphones className="w-3 h-3" />
                        {unit.durationMin || unit.duration} min · Conv. {index + 1}
                      </div>

                      <h3 className="text-base sm:text-3xl font-bold text-slate-900 leading-snug group-hover:text-cyan-700 transition-colors">
                        {unit.title}
                      </h3>
                    </div>
                  </div>

                  {/* Descripción — visible en todos los tamaños */}
                  <p className="text-sm text-slate-600 leading-relaxed mb-1">
                    {unit.objective}
                  </p>
                  {unit.source && (
                    <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-6">
                      <span className="font-medium text-slate-500">{unit.source.book}</span> · {unit.source.author}
                    </p>
                  )}

                  {/* CTA */}
                  <div className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-cyan-600 group-hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors shadow-sm text-sm">
                    <Play className="w-3.5 h-3.5 fill-white" />
                    Escuchar conversación
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
          </div>
        </section>

        {/* ===== CONTENIDO EDITORIAL - SEO ===== */}
        <section className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-16 bg-slate-50">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-4 sm:px-12 py-6 sm:py-14">
            
            {/* ¿Para quién es? */}
            <div className="mb-5 sm:mb-12">
              <h2 className="text-lg sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">
                ¿Para quién es este programa?
              </h2>
              {program.audienceDetail ? (
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                  {program.audienceDetail}
                </p>
              ) : (
                <>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4">
                    Este micro-programa está diseñado para <strong>{program.audienceDescription || 'profesionales y ejecutivos'}</strong> que buscan estructurar su proceso de influencia con metodologías consultivas validadas. No es un curso de técnicas transaccionales; es una guía práctica para diagnosticar necesidades reales, construir argumentos sólidos y facilitar decisiones sin manipular.
                  </p>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                    Ideal para nivel intermedio-senior que ya domina fundamentos de su campo y quiere incorporar frameworks de {program.sources?.slice(0, 2).map(s => s.author).join(', ')} y otros expertos reconocidos.
                  </p>
                </>
              )}
            </div>

            {/* Qué aprenderás */}
            <div className="mb-5 sm:mb-12">
              <h2 className="text-lg sm:text-3xl font-bold text-slate-900 mb-4 sm:mb-6">
                Qué aprenderás
              </h2>
              <div className="space-y-6">
                {program.units.map((unit, index) => (
                  <div key={unit.slug} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-semibold text-sm mt-1">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900 mb-2">{unit.title}</h3>
                      <p className="text-slate-600 text-base leading-relaxed mb-2">{unit.objective}</p>
                      <p className="text-sm text-slate-500">
                        <span className="font-medium">Basado en:</span> {unit.source ? `"${unit.source.book}" de ${unit.source.author}` : 'metodologías aplicadas'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-base text-slate-700 leading-relaxed mt-6">
                <strong>Resultado esperado:</strong> {program.outcomeDetailed || program.outcome}
              </p>
            </div>

            {/* Cómo funciona */}
            <div className="mb-5 sm:mb-12">
              <h2 className="text-lg sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">
                Cómo funciona
              </h2>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                {program.howItWorks ? (
                  <ol className="space-y-3 text-base text-slate-700">
                    {program.howItWorks.map((step, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="font-bold text-cyan-600">{step.step}.</span>
                        <span><strong>{step.title}</strong> — {step.description}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <ol className="space-y-3 text-base text-slate-700">
                    <li className="flex gap-3">
                      <span className="font-bold text-cyan-600">1.</span>
                      <span><strong>Escucha la conversación</strong> ({program.durationMin} minutos total divididos en {program.units.length} unidades).</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-cyan-600">2.</span>
                      <span><strong>Contrasta tu criterio</strong> con las ideas presentadas. No memorices; cuestiona.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-cyan-600">3.</span>
                      <span><strong>Aplica en 48 horas</strong>. Cada unidad incluye un ejercicio breve.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-cyan-600">4.</span>
                      <span><strong>Avanza a tu ritmo</strong>. Escucha en tu commute o pausas de trabajo.</span>
                    </li>
                  </ol>
                )}
                <p className="text-sm text-slate-600 mt-4">
                  <strong>Progreso automático:</strong> Tu avance se guarda en tu navegador.
                </p>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-5 sm:mb-12">
              <h2 className="text-lg sm:text-3xl font-bold text-slate-900 mb-4 sm:mb-6">
                Preguntas frecuentes
              </h2>
              <div className="space-y-4">
                <details className="group bg-slate-50 border border-slate-200 rounded-lg p-5 cursor-pointer hover:bg-slate-100 transition-colors">
                  <summary className="font-semibold text-base sm:text-lg text-slate-900 list-none flex items-center justify-between">
                    <span>¿Cuánto tiempo debo dedicar?</span>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-slate-700 mt-3 leading-relaxed">
                    {program.durationMin} minutos de audio total. Puedes escuchar una unidad al día. La clave no es velocidad sino aplicación.
                  </p>
                </details>

                <details className="group bg-slate-50 border border-slate-200 rounded-lg p-5 cursor-pointer hover:bg-slate-100 transition-colors">
                  <summary className="font-semibold text-base sm:text-lg text-slate-900 list-none flex items-center justify-between">
                    <span>¿Necesito conocimientos previos?</span>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-slate-700 mt-3 leading-relaxed">
                    Sí. Este programa asume experiencia básica en tu campo. No es un curso de fundamentos.
                  </p>
                </details>

                <details className="group bg-slate-50 border border-slate-200 rounded-lg p-5 cursor-pointer hover:bg-slate-100 transition-colors">
                  <summary className="font-semibold text-base sm:text-lg text-slate-900 list-none flex items-center justify-between">
                    <span>¿Es gratis o de pago?</span>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-slate-700 mt-3 leading-relaxed">
                    Este programa es parte de EduTech Pulse Lab, un laboratorio de investigación. Por ahora, el acceso es gratuito para usuarios registrados.
                  </p>
                </details>

                <details className="group bg-slate-50 border border-slate-200 rounded-lg p-5 cursor-pointer hover:bg-slate-100 transition-colors">
                  <summary className="font-semibold text-base sm:text-lg text-slate-900 list-none flex items-center justify-between">
                    <span>¿Obtendré un certificado?</span>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-slate-700 mt-3 leading-relaxed">
                    No. El objetivo es desarrollar criterio aplicable, no coleccionar diplomas.
                  </p>
                </details>

                {/* T13 — FAQ insignia (solo si el programa la tiene) */}
                {program.badgeEnabled && (
                  <details className="group bg-slate-50 border border-slate-200 rounded-lg p-5 cursor-pointer hover:bg-slate-100 transition-colors">
                    <summary className="font-semibold text-base sm:text-lg text-slate-900 list-none flex items-center justify-between">
                      <span>¿Hay una insignia digital?</span>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                    </summary>
                    <p className="text-slate-700 mt-3 leading-relaxed">
                      Sí. Al completar las {program.requiredConversationCount || program.units.length} conversaciones y el ejercicio final, podrás reclamar una insignia digital verificable. Necesitas una cuenta gratuita para guardarla y obtener tu enlace compartible.
                    </p>
                  </details>
                )}
              </div>
            </div>

            {/* Referencias bibliográficas */}
            {program.sources && program.sources.length > 0 && (
              <div className="border-t border-slate-200 pt-5 sm:pt-8 mb-6 sm:mb-10">
                <h3 className="text-xl font-bold text-slate-900 mb-4">
                  Inspirado en estas obras
                </h3>
                <div className="space-y-3">
                  {program.sources.map((source, index) => (
                    <div key={index} className="flex gap-3 text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-2 flex-shrink-0"></span>
                      <span>
                        <strong className="text-slate-900">{source.book}</strong> por {source.author}
                        {source.institution && <span className="text-slate-500"> ({source.institution})</span>}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-600 mt-4 leading-relaxed">
                  Este programa NO reemplaza la lectura de los libros originales. Es una síntesis aplicada.
                </p>
              </div>
            )}

            {/* Enlace al blog */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 sm:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">
                Profundiza con artículos relacionados
              </h3>
              <p className="text-sm sm:text-base text-slate-700 mb-4 leading-relaxed">
                Explora artículos en nuestro blog sobre {program.title.toLowerCase()}, metodologías consultivas y toma de decisiones.
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                Ver artículos del blog
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ===== T14-T15: EJERCICIO FINAL + INSIGNIA ===== */}
        {program.finalExerciseEnabled && (
          <section id="ejercicio-final" className={`max-w-7xl mx-auto px-4 sm:px-8 ${existingBadge ? 'py-4 sm:py-8' : 'py-8 sm:py-16'}`}>
            {completedUnits >= (program.requiredConversationCount || program.units.length) ? (
              <>
                {/* T14 — Aviso antes del ejercicio: solo si el usuario aún NO tiene badge emitido */}
                {!existingBadge && (
                  <div className="mb-6 p-4 bg-cyan-50 border border-cyan-200 rounded-lg flex items-start gap-3">
                    <Award className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-cyan-800 leading-relaxed">
                      Ya completaste el contenido. El siguiente paso es el ejercicio final. Podrás reclamar tu insignia al terminar e iniciar sesión para guardarla.
                    </p>
                  </div>
                )}
                {/* T15 — Componente ejercicio */}
                <FinalExercise
                  program={program}
                  isAuthenticated={isAuthenticated}
                  user={user}
                />
              </>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 sm:p-8 text-center">
                <Award className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-slate-700 mb-2">
                  Ejercicio final e insignia digital
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Completa las {program.requiredConversationCount || program.units.length} conversaciones para desbloquear el ejercicio final y reclamar tu insignia.
                </p>
                <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden w-48 mx-auto">
                  <div
                    className="h-full bg-cyan-600 rounded-full transition-all duration-500"
                    style={{ width: `${(completedUnits / (program.requiredConversationCount || program.units.length)) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {completedUnits} de {program.requiredConversationCount || program.units.length} conversaciones completadas
                </p>
              </div>
            )}
          </section>
        )}

        {/* ===== UNIDADES BLOQUEADAS (PRÓXIMAMENTE) ===== */}
        {program.units.some(u => u.locked === true) && (
          <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-16">
            <div className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-3xl font-bold text-slate-900 mb-2 sm:mb-3">
                Próximamente disponibles
              </h2>
              <p className="text-base text-slate-600">
                Más conversaciones en desarrollo para profundizar en estos temas.
              </p>
            </div>

            <div className="grid gap-6">
              {program.units.filter(u => u.locked === true).map((unit, index) => {
                const actualIndex = program.units.indexOf(unit) + 1;
                return (
                  <div
                    key={unit.slug}
                    className="block bg-white rounded-2xl shadow-sm transition-all duration-200 overflow-hidden opacity-60 border border-slate-200"
                  >
                    <div className="relative px-6 sm:px-10 pt-6 sm:pt-8 pb-6">
                      <div className="absolute top-6 right-6">
                        <Lock className="w-5 h-5 text-slate-400" />
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                          Conversación {actualIndex}
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="text-xs text-slate-400">
                          {unit.duration} min
                        </span>
                      </div>

                      <h3 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight mb-3 text-slate-500">
                        {unit.title}
                      </h3>

                      <p className="text-sm leading-relaxed text-slate-400 mb-4">
                        {unit.objective}
                      </p>

                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-400">
                          {unit.source && <>Basado en: {unit.source.author}</>}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ===== FOOTER COMPARTIR ===== */}
        <footer className="border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <p className="text-slate-600 text-sm sm:text-base mb-1">
                  ¿Te resultó útil este programa?
                </p>
                <p className="text-slate-500 text-xs sm:text-sm">
                  Compártelo con tu equipo o red profesional
                </p>
              </div>

              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium text-sm transition-colors group"
                aria-label="Compartir programa"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    URL copiada
                  </>
                ) : (
                  <>
                    Compartir esta experiencia
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
}
